import { useMemo, useState } from "react"
import { Box, useMediaQuery, Typography, Button } from "@mui/material"
import { styled, useTheme } from "@mui/material/styles"
import MainTable from "./MainTable"
import useModifiers from "./hooks/useModifiers"
import useFilteredData from "./hooks/useFilteredData"
import Header from "./components/Header"
import InquirySection from "./components/InquirySection"

const PageGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 300px',
  gridTemplateRows: 'auto 1fr',
  columnGap: theme.spacing(4),
  rowGap: theme.spacing(1),
  height: `calc(100vh - 64px)`, // Desktop Navbar height
  padding: theme.spacing(2, 4),
  boxSizing: 'border-box',
  overflow: 'hidden', // Contain all children
  [theme.breakpoints.down('md')]: {
    display: 'flex',
    flexDirection: 'column',
    gridTemplateColumns: '1fr',
    columnGap: 0,
    padding: theme.spacing(1, 2),
    height: `calc(100vh - 56px)`, // Mobile Navbar height
    minHeight: 0
  }
}))

function StockListPage() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const modifiers = useModifiers(data)
  const { selections, actions, options } = modifiers
  const filteredData = useFilteredData(data, selections)
  const dataByStock = useMemo(
    () => new Map(data.map((item) => [item.stock_no, item])),
    [data]
  )
  const selectedData = useMemo(() => {
    const selectedStocks = selections.selectedStocks
    if (!selectedStocks || selectedStocks.size === 0) return []
    const selectionsArray = []
    selectedStocks.forEach((stockNo) => {
      const item = dataByStock.get(stockNo)
      if (item) selectionsArray.push(item)
    })
    return selectionsArray
  }, [dataByStock, selections.selectedStocks])
  const hasSelection = selectedData.length > 0
  const summaryLabel = hasSelection ? "Selected" : "Viewing"
  const summaryCount = hasSelection ? selectedData.length : filteredData.length

  return (
    <PageGrid>
      {isMobile ? (
        <>
          <Box sx={{ flex: "0 0 auto" }}>
            <Header 
              setData={setData}
              setLoading={setLoading}
              loading={loading}
              modifiers={modifiers} 
            />

            {!loading && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  pl: 0.5,
                  py: 0.5,
                  color: "text.secondary"
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: 0.3 }}>
                    {summaryLabel}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "text.primary" }}>
                    {summaryCount}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    / {data.length} stones
                  </Typography>
                </Box>
                <Button
                  size="small"
                  color="inherit"
                  onClick={actions.reset}
                  sx={{
                    minWidth: "auto",
                    fontSize: "0.7rem",
                    color: "text.secondary",
                    "&:hover": { color: "text.primary" }
                  }}
                >
                  RESET
                </Button>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              flex: "1 1 auto",
              minHeight: 0,
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain"
            }}
          >
            {loading ? (
              <MainTable loading={true} modifiers={modifiers} filteredData={[]} isMobile={isMobile} />
            ) : (
              <MainTable modifiers={modifiers} filteredData={filteredData} isMobile={isMobile} />
            )}
          </Box>

          <InquirySection 
            loading={loading}
            filteredData={filteredData} 
            selections={selections}
            selectedData={selectedData} 
            totalCount={data.length}
            isMobile={isMobile}
            ctBounds={options?.ct}
          />
        </>
      ) : (
        <>
          <Header 
            setData={setData}
            setLoading={setLoading}
            loading={loading}
            modifiers={modifiers} 
          />

          {/* Loading state or Main Table */}
          {loading ? (
            <MainTable loading={true} modifiers={modifiers} filteredData={[]} isMobile={isMobile} />
          ) : (
            <MainTable modifiers={modifiers} filteredData={filteredData} isMobile={isMobile} />
          )}

          <InquirySection 
            loading={loading}
            filteredData={filteredData} 
            selections={selections}
            selectedData={selectedData} 
            totalCount={data.length}
            isMobile={isMobile}
            ctBounds={options?.ct}
          />
        </>
      )}
    </PageGrid>
  )
}

export default StockListPage
